import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Loader2, Calculator, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { Checkbox } from '@/components/ui/Checkbox';
import { 
  usePayRun, 
  useCreatePayRun, 
  useUpdatePayRun,
  useCalculatePayRun 
} from '@/hooks/usePayRuns';
import { useEmployees } from '@/hooks/useEmployees';
import { formatDate } from '@/lib/utils';

export function PayRunForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const { data: existingPayRun, isLoading: isLoadingPayRun } = usePayRun(id!);
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({ status: 'active' });
  const createPayRun = useCreatePayRun();
  const updatePayRun = useUpdatePayRun();
  const calculatePayRun = useCalculatePayRun();

  const employees = employeesData?.data || [];

  const [formData, setFormData] = useState({
    periodStartDate: '',
    periodEndDate: '',
    paymentDate: '',
    selectedEmployeeIds: [] as string[],
  });

  useEffect(() => {
    if (existingPayRun) {
      setFormData({
        periodStartDate: existingPayRun.periodStartDate,
        periodEndDate: existingPayRun.periodEndDate,
        paymentDate: existingPayRun.paymentDate,
        selectedEmployeeIds: existingPayRun.items?.map(item => item.employeeId) || [],
      });
    } else {
      // Set default dates for new pay run
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() - 13); // Previous fortnight start
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 13); // Previous fortnight end
      const paymentDate = new Date(endOfWeek);
      paymentDate.setDate(endOfWeek.getDate() + 2); // Payment 2 days after period end

      setFormData({
        periodStartDate: startOfWeek.toISOString().split('T')[0],
        periodEndDate: endOfWeek.toISOString().split('T')[0],
        paymentDate: paymentDate.toISOString().split('T')[0],
        selectedEmployeeIds: [],
      });
    }
  }, [existingPayRun]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeToggle = (employeeId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(employeeId)
        ? prev.selectedEmployeeIds.filter(id => id !== employeeId)
        : [...prev.selectedEmployeeIds, employeeId],
    }));
  };

  const handleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.length === employees.length
        ? []
        : employees.map(e => e.id),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.selectedEmployeeIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one employee',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (isEditing) {
        await updatePayRun.mutateAsync({ 
          id, 
          data: {
            periodStartDate: formData.periodStartDate,
            periodEndDate: formData.periodEndDate,
            paymentDate: formData.paymentDate,
          }
        });
        toast({
          title: 'Success',
          description: 'Pay run updated successfully',
          variant: 'success',
        });
        navigate(`/payroll/pay-runs/${id}`);
      } else {
        const newPayRun = await createPayRun.mutateAsync({
          periodStartDate: formData.periodStartDate,
          periodEndDate: formData.periodEndDate,
          paymentDate: formData.paymentDate,
          employeeIds: formData.selectedEmployeeIds,
        });
        toast({
          title: 'Success',
          description: 'Pay run created successfully',
          variant: 'success',
        });
        navigate(`/payroll/pay-runs/${newPayRun.id}`);
      }
    } catch {
      toast({
        title: 'Error',
        description: isEditing ? 'Failed to update pay run' : 'Failed to create pay run',
        variant: 'destructive',
      });
    }
  };

  const handleCalculate = async () => {
    if (!isEditing) return;
    
    try {
      await calculatePayRun.mutateAsync(id!);
      toast({
        title: 'Success',
        description: 'Pay run calculated successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to calculate pay run',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingPayRun || isLoadingEmployees) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isSubmitting = createPayRun.isPending || updatePayRun.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Pay Run' : 'Create Pay Run'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Update pay run details' : 'Set up a new pay run for your employees'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pay Period Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Pay Period Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="periodStartDate">Period Start Date *</Label>
                    <Input
                      id="periodStartDate"
                      type="date"
                      value={formData.periodStartDate}
                      onChange={(e) => handleChange('periodStartDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="periodEndDate">Period End Date *</Label>
                    <Input
                      id="periodEndDate"
                      type="date"
                      value={formData.periodEndDate}
                      onChange={(e) => handleChange('periodEndDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paymentDate">Payment Date *</Label>
                    <Input
                      id="paymentDate"
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => handleChange('paymentDate', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employee Selection */}
            {!isEditing && (
              <Card>
                <CardHeader>
                  <CardTitle>Select Employees</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.selectedEmployeeIds.length === employees.length && employees.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="font-medium">Select All Employees</span>
                    </label>
                  </div>
                  <div className="border rounded-lg divide-y">
                    {employees.map((employee) => (
                      <label
                        key={employee.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.selectedEmployeeIds.includes(employee.id)}
                          onCheckedChange={() => handleEmployeeToggle(employee.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{employee.firstName} {employee.lastName}</p>
                          <p className="text-sm text-muted-foreground">
                            {employee.code} • {employee.jobTitle}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {employees.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">
                      No active employees found
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Summary Card */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Period</p>
                  <p className="font-medium">
                    {formData.periodStartDate && formData.periodEndDate
                      ? `${formatDate(formData.periodStartDate)} - ${formatDate(formData.periodEndDate)}`
                      : 'Not set'}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Payment Date</p>
                  <p className="font-medium">
                    {formData.paymentDate ? formatDate(formData.paymentDate) : 'Not set'}
                  </p>
                </div>
                {!isEditing && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Selected Employees</p>
                    <p className="font-medium">{formData.selectedEmployeeIds.length}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCalculate}
              disabled={calculatePayRun.isPending}
            >
              {calculatePayRun.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Calculator className="mr-2 h-4 w-4" />
              Recalculate
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Update Pay Run' : 'Create Pay Run'}
          </Button>
        </div>
      </form>
    </div>
  );
}
